// ============================================================================
// INTERRUPTEUR WEB — une option qu'on ACTIVE, avec une cible de clic à la taille du doigt.
//
// ⚠️ POURQUOI PAS UNE CASE À COCHER NATIVE. Elle mesure ~13 px, ignore la gamme de tailles du thème et
// ne se prête à aucune personnalisation fiable (`accentColor` ne colore que la coche). Sur une option
// qui engage — « prendre un instantané avant de migrer » —, une cible minuscule est un vrai défaut :
// on la rate, et rien ne dit qu'on l'a ratée. Constaté par l'utilisateur sur la modale de migration.
//
// ⚠️ ACCESSIBILITÉ : c'est un vrai `<button role="switch">` avec `aria-checked`, pas un `<div>` peint.
// Il reçoit le focus clavier, réagit à Entrée et Espace sans code supplémentaire, et un lecteur d'écran
// annonce « activé / désactivé » — ce qu'un div stylé ne fait pas.
//
// ⚠️ GÉOMÉTRIE RELEVÉE, PAS INVENTÉE : 38×22 avec une pastille de 16. C'est la mesure des 43
// interrupteurs de la maquette du back-office, seule source dessinée dont nous disposions. La valeur
// précédente (42×24) ne venait d'aucune maquette ; l'aligner évite deux tailles d'interrupteur selon
// l'app, qui est exactement la Forme A du catalogue (deux vérités qui divergent en silence).
// ============================================================================
import { useId, type CSSProperties, type ReactNode } from 'react';
import { OZ_INTERACTIVE, OZ_INTERACTIVE_SURFACE } from './interactive';

/** Piste et pastille : la position haute du curseur se déduit des trois autres, jamais saisie deux fois. */
const PISTE_L = 38, PISTE_H = 22, PASTILLE = 16, MARGE = 3;

export interface SwitchProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  /** Libellé court, en gras à côté de l'interrupteur. */
  label: ReactNode;
  /** Explication : sous l'ensemble en disposition `stack`, dans la colonne de texte en `card`. */
  hint?: ReactNode;
  disabled?: boolean;
  style?: CSSProperties;
  /**
   * `stack` (défaut) : interrupteur + libellé sur une ligne, explication dessous sur toute la largeur.
   * `card` : le tout dans une rangée bordée cliquable, explication sous le libellé — c'est la forme de
   * la maquette pour un DRAPEAU d'article (« Diffusé sur le site internet »), où la bordure suffit à
   * rattacher le texte à la commande.
   */
  layout?: 'stack' | 'card';
}

/**
 * Interrupteur + libellé.
 *
 * ⚠️ MISE EN PAGE (`stack`) : l'explication passe SOUS la ligne de l'interrupteur, sur toute la largeur —
 * elle n'est pas collée à droite de la commande. Un texte long placé à côté d'une commande étroite
 * produit une colonne déséquilibrée : la commande paraît perdue à gauche, et le texte, décalé, ne se lit
 * plus comme lui appartenant (retour utilisateur 2026-08-11 sur la modale de migration). La disposition
 * `card` échappe à ce défaut autrement : la bordure fait le groupement que l'alignement ne fait pas.
 */
export function Switch({ checked, onChange, label, hint, disabled = false, style, layout = 'stack' }: SwitchProps) {
  const buttonId = useId();
  const piste: CSSProperties = {
    width: PISTE_L, height: PISTE_H, borderRadius: 'var(--r-pill)', border: 'none', padding: 0,
    // ⚠️ `inline-flex` EXPLICITE : en disposition `card` la piste est un `<span>`, et un span `inline`
    // ignore largeur et hauteur — la piste n’a alors aucune boîte, et les deux états se confondent.
    flexShrink: 0, position: 'relative', display: 'inline-flex',
    background: checked ? 'var(--teal-500)' : 'var(--ink-200)',
    transition: 'background .15s ease',
  };
  // Le curseur est décalé par `left` : une transition sur une seule propriété reste fluide même sur une
  // machine chargée, là où un `transform` combiné à un changement de fond peut saccader.
  const curseur: CSSProperties = {
    position: 'absolute', top: (PISTE_H - PASTILLE) / 2, left: checked ? PISTE_L - MARGE - PASTILLE : MARGE,
    width: PASTILLE, height: PASTILLE, borderRadius: '50%', background: '#fff',
    boxShadow: '0 1px 3px rgba(0,0,0,.28)', transition: 'left .15s ease',
  };
  const inerte: CSSProperties = { cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1 };
  const basculer = () => { if (!disabled) onChange(!checked); };

  if (layout === 'card') {
    return (
      <button
        type="button" role="switch" aria-checked={checked} disabled={disabled}
        // ⚠️ PAS `OZ_INTERACTIVE` ICI : son `brightness()` éclaircirait une carte déjà blanche et
        // EFFACERAIT sa bordure pâle — le bloc paraissait disparaître sous le pointeur au moment même
        // du clic. La variante `surface` pose un voile par-dessus le fond et laisse le cadre intact.
        className={OZ_INTERACTIVE_SURFACE}
        onClick={basculer}
        style={{
          display: 'flex', alignItems: 'center', gap: 11, padding: '12px 14px', width: '100%',
          boxSizing: 'border-box', textAlign: 'left', border: '1px solid var(--ink-200)',
          borderRadius: 'var(--r-sm)', background: 'var(--paper)', ...inerte, ...style,
        }}
      >
        <span style={piste}><span style={curseur} /></span>
        <span style={{ minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: '13.5px', fontWeight: 600, color: 'var(--ink-900)' }}>
            {label}
          </span>
          {hint && (
            <span style={{ display: 'block', fontSize: '12px', color: 'var(--ink-500)', lineHeight: 1.45, marginTop: 2 }}>
              {hint}
            </span>
          )}
        </span>
      </button>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, ...style }}>
      {/* ⚠️ LE LIBELLÉ EST UN `<label for>`, VOISIN ET NON PARENT : il NOMME le bouton pour une aide technique, et son
          clic active le bouton une seule fois. Englobant, il l'aurait activé deux fois (clic sur le label + clic
          propagé) ; un `<span>` à `onClick` laissait le bouton sans nom et doublait au pointeur seul ce qu'il fait. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button
          id={buttonId} type="button" role="switch" aria-checked={checked} disabled={disabled} className={OZ_INTERACTIVE}
          onClick={basculer} style={{ ...piste, ...inerte }}
        >
          <span style={curseur} />
        </button>
        <label
          htmlFor={buttonId}
          style={{ fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-800)', cursor: inerte.cursor }}
        >
          {label}
        </label>
      </div>
      {/* ⚠️ `--ink-500`, PAS `--ink500` (corrigé 2026-09-08). Le jeton n'existe sous aucun nom sans
          tiret : la variable ne résolvant rien, c'est le REPLI qui peignait ce texte depuis toujours —
          donc la couleur du thème CLAIR, y compris en thème sombre, dans un composant que les quatre
          surfaces consomment. Une faute de frappe d'un caractère, invisible au typecheck comme au
          lint, et que seule la comparaison repli/jeton a fait sortir. */}
      {hint && (
        <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)', lineHeight: 1.55 }}>{hint}</div>
      )}
    </div>
  );
}
